import { useCallback, useEffect, useMemo, useState } from "react"

import { BehaviorSubject, concat, of } from "rxjs"
import { catchError, distinctUntilChanged, map, switchMap } from "rxjs/operators"

import NotificationApplicationUserData from "./NotificationApplicationUserData"
import NotificationApplicationUsersGridData from "./NotificationApplicationUsersGridData"
import NotificationApplicationUsersSearchParams from "./NotificationApplicationUsersSearchParams"
import { GetNotificationApplicationUsersObservable } from "./NotificationContactsProcess"
import handleAPIError from "../../../utils/handleAPIError"
import ApiResult from "../../ApiResult"

const PAGE_SIZE = 3

function useNotificationApplicationUsersLoad(poolId: number) {
  const [gridData, setGridData] = useState<NotificationApplicationUsersGridData>({
    Records: [],
    Pages: 0,
    Total: 0,
  })
  const [loadingApplicationUsers, setLoadingApplicationUsers] = useState(false)

  const initialSearchParams = useMemo(
    () => ({
      PoolId: poolId,
      SearchFor: "",
      SearchBy: "",
      PageNumber: 1,
      PageSize: PAGE_SIZE,
    }),
    [poolId]
  )

  const page$ = useMemo(
    () => new BehaviorSubject<NotificationApplicationUsersSearchParams>(initialSearchParams),
    [initialSearchParams]
  )

  const handleApiResponse = useCallback(
    ({ loading, result, error }: ApiResult<NotificationApplicationUsersGridData>) => {
      setLoadingApplicationUsers(loading)

      if (!loading && result) {
        const parsedRecords = result.Records.map((record: NotificationApplicationUserData) => ({
          ...record,
          selected: false,
        }))

        setGridData({
          ...result,
          Records: parsedRecords ?? [],
          Pages: result.Pages ?? 0,
          Total: result.Total ?? 0,
        })
      }

      if (error) {
        handleAPIError(error)
      }
    },
    []
  )

  useEffect(() => {
    const fetchApplicationUsers = (params: NotificationApplicationUsersSearchParams) => {
      return concat(
        of({ loading: true }),
        GetNotificationApplicationUsersObservable(params).pipe(
          map((result) => ({ loading: false, result })),
          catchError((error) =>
            of({
              loading: false,
              result: {
                Records: [],
                Pages: 0,
                Total: 0,
              },
              error,
            })
          )
        )
      )
    }

    const subscription = page$
      .pipe(distinctUntilChanged(), switchMap(fetchApplicationUsers))
      .subscribe(handleApiResponse, (error) => handleAPIError(error, () => setLoadingApplicationUsers(false)))

    return () => subscription.unsubscribe()
  }, [page$, handleApiResponse])

  const updateSearchParams = useCallback(
    (updates: Partial<NotificationApplicationUsersSearchParams>) => {
      page$.next({ ...page$.value, ...updates })
    },
    [page$]
  )

  const setPage = useCallback(
    (page: number) => {
      updateSearchParams({ PageNumber: page })
    },
    [updateSearchParams]
  )

  const searchApplicationUsers = useCallback(
    (searchField: string, searchText: string) => {
      updateSearchParams({ SearchFor: searchText, SearchBy: searchField, PageNumber: 1 })
    },
    [updateSearchParams]
  )

  const reloadApplicationUsers = useCallback(() => {
    updateSearchParams({ PageNumber: 1 })
  }, [updateSearchParams])

  const updateSelectedUsers = useCallback((userIds: number[], checked: boolean, isSingleSelect = false) => {
    setGridData((prev) => ({
      ...prev,
      Records: prev.Records?.map((c) =>
        // eslint-disable-next-line no-nested-ternary
        userIds.includes(c.ApplicationUserID)
          ? { ...c, selected: checked }
          : isSingleSelect
            ? { ...c, selected: false }
            : c
      ),
    }))
  }, [])

  const selectApplicationUsers = useCallback(
    (userIds: number[], checked: boolean) => {
      updateSelectedUsers(userIds, checked)
    },
    [updateSelectedUsers]
  )

  const selectOneApplicationUser = useCallback(
    (userIds: number[], checked: boolean) => {
      updateSelectedUsers(userIds, checked, true)
    },
    [updateSelectedUsers]
  )

  const selectedApplicationUsers = useMemo(() => gridData.Records.filter((c) => c.selected), [gridData])

  return {
    loadingApplicationUsers,
    gridData,
    selectedApplicationUsers,
    pageNumber: page$.value.PageNumber,
    setPage,
    selectApplicationUsers,
    selectOneApplicationUser,
    reloadApplicationUsers,
    searchApplicationUsers,
  }
}

export default useNotificationApplicationUsersLoad
